import {
  characteristicPayloadSchema,
  characteristicReviewSchema,
  nnppMatchingRunSchema,
  type NnppSelectedSpecification,
  nnppSelectedSpecificationSchema,
  nullableString,
  sha256String,
} from 'types/nnpp/common/nnppCommonSchemas'
import { z } from 'zod'

export const nnppDocumentStatusSchema = z.enum([
  'uploading',
  'queued',
  'processing',
  'completed',
  'failed',
])

/**
 * Linking statuses follow a completed review while DHB links the report;
 * `needs_relationship_review` pauses linking until a person decides a link.
 */
const nnppVerificationStatusSchema = z.enum([
  'not_started',
  'in_progress',
  'linking',
  'linking_failed',
  'needs_relationship_review',
  'verified',
])

const workflowStatusSchema = z.enum([
  'not_started',
  'uploading',
  'queued',
  'processing',
  'completed',
  'verification',
  'summary',
  'failed',
])

const nextStepSchema = z.enum([
  'upload',
  'start',
  'wait',
  'retry',
  'verify',
  'summary',
])

export const nnppDocumentKindSchema = z.enum(['report', 'specification'])

export const nnppSpecificationOptionSchema = z
  .object({
    source_id: z.string().min(1).nullable(),
    document_id: z.string().min(1),
    extraction_key: sha256String,
    filename: z.string(),
    requirement_count: z.number().int().nonnegative(),
    check_count: z.number().int().nonnegative(),
    pending_review_count: z.number().int().nonnegative(),
    ready: z.boolean(),
    blocking_reasons: z.array(z.string()),
    extracted_at: z.string().datetime({ offset: true }),
  })
  .strict()

export const nnppSpecificationsResponseSchema = z
  .object({ items: z.array(nnppSpecificationOptionSchema) })
  .strict()

const uniqueSelectedSpecifications = (
  selectedSpecifications: readonly NnppSelectedSpecification[],
  context: z.RefinementCtx
) => {
  const sourceIds = selectedSpecifications.map(({ source_id }) => source_id)
  if (new Set(sourceIds).size !== sourceIds.length) {
    context.addIssue({
      code: 'custom',
      message: 'Select only one extraction for each requirement set.',
      path: ['selected_specifications'],
    })
  }
}

/** The canonical occurrence fields returned by the import-document list API. */
export const nnppImportDocumentSchema = z.object({
  import_id: z.string(),
  import_document_id: z.string(),
  source_document_id: nullableString,
  organization_id: nullableString,
  name: z.string(),
  import_name: nullableString,
  site_id: nullableString,
  supplier_site: nullableString,
  filename: z.string(),
  document_kind: nnppDocumentKindSchema,
  upload_status: z.enum(['pending', 'completed']),
  processing_status: nnppDocumentStatusSchema,
  verification_status: nnppVerificationStatusSchema,
  matching_run: nnppMatchingRunSchema,
  processing_stage: z
    .enum(['upload', 'ocr', 'extraction', 'completed', 'failed'])
    .nullable()
    .default(null),
  current_page: z.number().int().nonnegative().nullable().default(null),
  total_pages: z.number().int().nonnegative().nullable().default(null),
  percent_complete: z.number().min(0).max(100),
  created_at: z.string(),
  updated_at: z.string(),
})

const uploadStateSchema = z.object({
  status: z.enum(['pending', 'completed']),
  object_name: z.string(),
  size_bytes: z.number().positive(),
  content_sha256: z.string(),
  completed_at: nullableString,
})

const workflowSchema = z.object({
  status: workflowStatusSchema,
  next_step: nextStepSchema,
  available_actions: z.array(z.string()),
})

/** The direct detail response; it intentionally has no wrapper object. */
export const nnppImportDocumentDetailResponseSchema = z.object({
  ...nnppImportDocumentSchema.shape,
  source_path: nullableString,
  size_bytes: z.number().positive(),
  content_sha256: z.string(),
  staging_object: z.string(),
  upload_url: nullableString,
  upload: uploadStateSchema,
  upload_completed_at: nullableString,
  workflow: workflowSchema,
  combined_extraction_uri: nullableString,
  combined_extraction_manifest_uri: nullableString,
  extraction_key: nullableString.default(null),
  error_message: nullableString,
  state_version: z.number().int().nonnegative(),
})

const createdImportDocumentSchema = z.object({
  import_document_id: z.string(),
  filename: z.string(),
})

const createdImportSchema = z.object({
  import_id: z.string(),
  name: z.string(),
  documents: z.array(createdImportDocumentSchema),
})

const uploadTargetSchema = z.object({
  import_document_id: z.string(),
  upload_url: z.string().url(),
  object_name: z.string(),
})

/** The create endpoint only accepts the wrapped DHB v1 response shape. */
export const createNnppImportResponseSchema = z
  .object({
    import: createdImportSchema,
    uploads: z.array(uploadTargetSchema),
  })
  .transform(({ import: value, uploads }) => ({ ...value, uploads }))

export const nnppImportDocumentListResponseSchema = z.object({
  items: z.array(nnppImportDocumentSchema),
  next_cursor: z.string().nullable(),
})

export const nnppImportVerificationResponseSchema = z.object({
  import_id: z.string(),
  import_document_id: z.string(),
  import_name: z.string(),
  filename: z.string(),
  document_kind: z.literal('report'),
  processing_status: z.literal('completed'),
  extraction_key: sha256String,
  verification_status: nnppVerificationStatusSchema,
  matching_run: nnppMatchingRunSchema,
  state_version: z.number().int().nonnegative(),
  source: z.object({
    source_document_id: z.string(),
  }),
  characteristics: z.array(characteristicReviewSchema),
  total_characteristics: z.number().int().nonnegative(),
  reviewed_characteristics: z.number().int().nonnegative(),
  pending_review_count: z.number().int().nonnegative(),
  pending_review_reasons: z.array(z.string()),
  completion_eligible: z.boolean(),
})

export const nnppImportVerificationCountsResponseSchema = z
  .object({
    state_version: z.number().int().nonnegative(),
    verification_status: nnppVerificationStatusSchema,
    total_characteristics: z.number().int().nonnegative(),
    reviewed_characteristics: z.number().int().nonnegative(),
    pending_review_count: z.number().int().nonnegative(),
    completion_eligible: z.boolean(),
  })
  .strict()

export const nnppImportVerificationReviewResponseSchema = z.object({
  state_version: z.number().int().nonnegative(),
  verification_status: nnppVerificationStatusSchema,
  review: characteristicReviewSchema,
})

export const nnppImportVerificationReviewRequestSchema = z.object({
  state_version: z.number().int().nonnegative(),
  review: z.object({
    target_kind: z.literal('characteristic'),
    extraction_key: sha256String,
    extraction_run_id: z.string().regex(/^\d{8}T\d{12}Z-[a-z0-9]{6}$/),
    target_row_id: z.string(),
    original_payload_sha256: sha256String,
    resolved_review_reasons: z.array(z.string()).min(1),
    reviewed_payload: characteristicPayloadSchema,
  }),
})

export const NNPP_IMPORT_REVIEW_BATCH_LIMIT = 50

export const nnppImportVerificationBatchReviewRequestSchema = z.object({
  reviews: z
    .array(nnppImportVerificationReviewRequestSchema.shape.review)
    .min(1)
    .max(NNPP_IMPORT_REVIEW_BATCH_LIMIT),
})

export const nnppImportVerificationBatchReviewResponseSchema = z.object({
  state_version: z.number().int().nonnegative(),
  verification_status: nnppVerificationStatusSchema,
  reviews: z.array(characteristicReviewSchema),
})

export const nnppImportVerificationCompleteRequestSchema = z
  .object({
    extraction_key: sha256String,
    state_version: z.number().int().nonnegative(),
    selected_specifications: z.array(nnppSelectedSpecificationSchema).min(1),
  })
  .superRefine((request, context) =>
    uniqueSelectedSpecifications(request.selected_specifications, context)
  )

const identityEntitySchema = z
  .object({
    entity_id: z.string(),
    entity_type: z.string(),
    display_name: z.string(),
  })
  .strict()

const identityEvidenceSchema = z
  .object({
    document_id: z.string(),
    current_document: z.boolean(),
    characteristic_id: nullableString,
    identifier_type: nullableString,
    raw_value: nullableString,
    pdf_page_number: z.number().int().nullable(),
  })
  .strict()

const identityDecisionSchema = z
  .object({
    group_id: sha256String,
    choice: z.enum(['option', 'none']),
    target_entity_id: nullableString.optional(),
  })
  .strict()

const identityDetailSchema = z
  .object({
    kind: z.enum(['PART', 'LOT', 'HEAT']),
    status: z.enum(['found', 'new', 'needs_review', 'unlinked', 'missing']),
    values: z.array(
      z
        .object({
          characteristic_id: nullableString,
          identifier_type: nullableString,
          raw_value: nullableString,
          pdf_page_number: z.number().int().nullable(),
          editable: z.boolean(),
        })
        .strict()
    ),
    entity: identityEntitySchema.nullable(),
    other_document_count: z.number().int().nonnegative(),
    group_ids: z.array(z.string()),
    reason: nullableString,
  })
  .strict()

export const nnppIdentityReviewResponseSchema = z
  .object({
    import_id: z.string(),
    import_document_id: z.string(),
    extraction_key: sha256String,
    verification_status: nnppVerificationStatusSchema,
    status: z.enum(['clear', 'needs_review']),
    details: z.array(identityDetailSchema),
    groups: z.array(
      z
        .object({
          group_id: sha256String,
          kind: z.enum(['choose', 'confirm', 'conflict']),
          relationship_type: z.enum([
            'REPORTS_ON',
            'PRODUCED_IN',
            'DERIVED_FROM',
          ]),
          subject: identityEntitySchema,
          reason_code: z.string(),
          reason: z.string(),
          options: z
            .array(
              z
                .object({
                  relationship_id: sha256String,
                  target: identityEntitySchema,
                  source: z.enum(['this_document', 'existing']),
                  evidence: z.array(identityEvidenceSchema),
                })
                .strict()
            )
            .min(1),
          allow_none: z.boolean(),
          decision: identityDecisionSchema.nullable(),
        })
        .strict()
    ),
    unresolved_group_count: z.number().int().nonnegative(),
  })
  .strict()

export const nnppCorrectIdentifierRequestSchema = z
  .object({
    extraction_key: sha256String,
    characteristic_id: z.string().min(1),
    value: z.string().trim().min(1).max(512),
  })
  .strict()

export const nnppSaveIdentityDecisionsRequestSchema = z
  .object({
    extraction_key: sha256String,
    decisions: z.array(identityDecisionSchema),
  })
  .strict()

export const nnppImportDocumentDeletionSchema = z.object({
  import_id: z.string(),
  import_document_id: z.string(),
  filename: z.string(),
  document_kind: nnppDocumentKindSchema,
  state_version: z.number().int().nonnegative(),
  deletable: z.boolean(),
  blocking_reasons: z.array(z.string()),
  reviewed_count: z.number().int().nonnegative(),
  retains_shared_content: z.boolean(),
  referencing_reports: z.array(z.string()),
  other_organization_reference_count: z.number().int().nonnegative(),
})

export type NnppImportDocumentDeletion = z.infer<
  typeof nnppImportDocumentDeletionSchema
>

export type NnppDocumentStatus = z.infer<typeof nnppDocumentStatusSchema>
export type NnppImportDocument = z.infer<typeof nnppImportDocumentSchema>
export type NnppImportDocumentDetail = z.infer<
  typeof nnppImportDocumentDetailResponseSchema
>
export type NnppImportDocumentPage = z.infer<
  typeof nnppImportDocumentListResponseSchema
>
export type NnppCreatedImport = z.infer<typeof createNnppImportResponseSchema>
export type NnppImportVerification = z.infer<
  typeof nnppImportVerificationResponseSchema
>
export type NnppImportVerificationReviewResponse = z.infer<
  typeof nnppImportVerificationReviewResponseSchema
>
export type NnppImportVerificationCounts = z.infer<
  typeof nnppImportVerificationCountsResponseSchema
>
export type NnppImportVerificationReviewRequest = z.infer<
  typeof nnppImportVerificationReviewRequestSchema
>
export type NnppImportVerificationBatchReviewRequest = z.infer<
  typeof nnppImportVerificationBatchReviewRequestSchema
>
export type NnppImportVerificationBatchReviewResponse = z.infer<
  typeof nnppImportVerificationBatchReviewResponseSchema
>
export type NnppImportVerificationCompleteRequest = z.infer<
  typeof nnppImportVerificationCompleteRequestSchema
>
export type NnppDocumentKind = z.infer<typeof nnppDocumentKindSchema>

export type NnppSpecificationOption = z.infer<
  typeof nnppSpecificationOptionSchema
>
export type NnppIdentityReview = z.infer<
  typeof nnppIdentityReviewResponseSchema
>
export type NnppIdentityGroup = NnppIdentityReview['groups'][number]
export type NnppIdentityDetail = NnppIdentityReview['details'][number]
export type NnppCorrectIdentifierRequest = z.infer<
  typeof nnppCorrectIdentifierRequestSchema
>
export type NnppIdentityDecision = z.infer<typeof identityDecisionSchema>
export type NnppSaveIdentityDecisionsRequest = z.infer<
  typeof nnppSaveIdentityDecisionsRequestSchema
>
